/**
 * 导航管理 Composable - 单例模式
 * 提供统一的导航管理功能
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { invariantDev } from '@/utils/invariant';

// 全局单例状态
const pageList = ref<PageItem[]>([
  {
    name: 'main',
    path: '/app',
    title: '开始创作',
    icon: 'Microphone'
  },
  {
    name: 'tasks',
    path: '/tasks',
    title: '转换记录',
    icon: 'List'
  },
  {
    name: 'batch',
    path: '/batch',
    title: '批量转换',
    icon: 'Files'
  },
  {
    name: 'dubbing',
    path: '/dubbing',
    title: '短剧配音',
    icon: 'VideoCamera',
    badge: 'HOT'
  },
  {
    name: 'library',
    path: '/library',
    title: '作品库',
    icon: 'FolderOpened'
  },
  {
    name: 'referral',
    path: '/referral',
    title: '推广赚钱',
    icon: 'Promotion',
    badge: 'NEW'
  },
  {
    name: 'docs',
    path: '/docs',
    title: '文档',
    icon: 'Document'
  },
  // 设置页面 - 已隐藏
  // 原因：Web 版本中设置页面的配置项主要针对桌面版或自托管场景，
  // 对普通 Web 用户来说过于技术化且不常用。
  // 主题切换等常用功能已在 header 右侧提供独立按钮。
  // 如需访问设置页面，可直接通过 URL /settings 访问。
  {
    name: 'settings',
    path: '/settings',
    title: '设置',
    icon: 'Setting',
    meta: {
      hidden: true  // 隐藏设置导航项，但保留路由功能
    }
  }
]);

// let isInitialized = false; // 暂时不需要

export interface PageItem {
  name: string;
  path: string;
  title: string;
  icon?: string;
  badge?: string | number;
  meta?: {
    requiresAuth?: boolean;
    hidden?: boolean;
    [key: string]: any;
  };
}

export interface UseNavigationReturn {
  // 状态
  pageList: Ref<PageItem[]>;
  currentPath: ComputedRef<string>;
  currentPage: ComputedRef<PageItem | undefined>;

  // 计算属性
  visiblePages: ComputedRef<PageItem[]>;
  mobilePageList: ComputedRef<PageItem[]>;

  // 方法
  navigateTo: (path: string) => Promise<void>;
  isCurrentPage: (path: string) => boolean;
  updatePageBadge: (path: string, badge: string | number) => void;
  addPage: (page: PageItem) => void;
  removePage: (path: string) => void;
}

/**
 * 导航管理 Hook
 */
export function useNavigation(): UseNavigationReturn {
  const router = useRouter();
  const route = useRoute();

  // Fail-Fast检查 - 确保路由已初始化
  invariantDev(router, 'Router must be available');
  invariantDev(route, 'Route must be available');

  // 计算属性
  const currentPath = computed(() => route.path);

  const currentPage = computed(() =>
    pageList.value.find(page => page.path === currentPath.value)
  );

  const visiblePages = computed(() =>
    pageList.value.filter(page => {
      // 过滤隐藏的页面
      if (page.meta?.hidden) return false;

      // 如果页面需要认证但用户未登录，则隐藏（游客用户可以看到所有页面）
      // if (page.meta?.requiresAuth && !userStore.isLoggedIn) return false;

      return true;
    })
  );

  // 移动端页面列表（简化版）
  const mobilePageList = computed(() =>
    visiblePages.value.filter(page =>
      ['/', '/tasks', '/batch', '/library'].includes(page.path)
    )
  );

  /**
   * 导航到指定路径
   */
  const navigateTo = async (path: string): Promise<void> => {
    try {
      if (currentPath.value !== path) {
        await router.push(path);
      }
    } catch (error) {
      console.error('导航失败:', error);
      throw error;
    }
  };

  /**
   * 检查是否为当前页面
   */
  const isCurrentPage = (path: string): boolean => {
    return currentPath.value === path;
  };

  /**
   * 更新页面徽章
   */
  const updatePageBadge = (path: string, badge: string | number) => {
    const page = pageList.value.find(p => p.path === path);
    if (page) {
      page.badge = badge;
    }
  };

  /**
   * 添加页面
   */
  const addPage = (page: PageItem) => {
    const existingIndex = pageList.value.findIndex(p => p.path === page.path);
    if (existingIndex >= 0) {
      // 更新现有页面
      pageList.value[existingIndex] = page;
    } else {
      // 添加新页面
      pageList.value.push(page);
    }
  };

  /**
   * 移除页面
   */
  const removePage = (path: string) => {
    const index = pageList.value.findIndex(p => p.path === path);
    if (index >= 0) {
      pageList.value.splice(index, 1);
    }
  };

  return {
    // 状态 - 返回只读的 ref
    pageList,
    currentPath,
    currentPage,

    // 计算属性
    visiblePages,
    mobilePageList,

    // 方法
    navigateTo,
    isCurrentPage,
    updatePageBadge,
    addPage,
    removePage
  };
}
